import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, FileSearch } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { parseInvoiceQR } from '@/lib/zatcaQrParser';
import { compareQrToBill, overall } from '@/lib/purchaseQrCompare';
import { QrInput } from './PurchaseQrInput';
import { VerificationResult } from './PurchaseVerificationResult';

const LINKS = [
  ['piv-link-einvoice', 'Verify E-Invoice', 'https://zatca.gov.sa/en/eServices/Pages/VerifytheValidityofthee-invoice.aspx'],
  ['piv-link-vat', 'Verify Supplier VAT', 'https://zatca.gov.sa/en/eServices/Pages/TaxpayerLookup.aspx'],
];
const since = (days) => new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);

// Read-only: reuses purchase bills + supplier VAT + the ZATCA QR decoder. Never writes anything.
export const PurchaseInvoiceVerification = () => {
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState({});
  const [q, setQ] = useState('');
  const [billId, setBillId] = useState('');
  const [qr, setQr] = useState('');
  const [supplierId, setSupplierId] = useState('');

  useEffect(() => {
    api.get('/suppliers').then((r) => setSuppliers(Object.fromEntries((Array.isArray(r.data) ? r.data : []).map((s) => [s.id, s])))).catch(() => {});
  }, []);
  useEffect(() => {
    const params = { start_date: since(supplierId ? 365 : 30), limit: 500, ...(supplierId ? { supplier_id: supplierId } : {}) };
    api.get('/accounting/bills', { params }).then((r) => setBills(r.data?.bills || [])).catch(() => setBills([]));
  }, [supplierId]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return bills.filter((b) => !t || [b.supplier_name, b.invoice_number, b.bill_number].some((v) => String(v || '').toLowerCase().includes(t))).slice(0, 50);
  }, [bills, q]);
  const bill = bills.find((b) => b.id === billId);
  const parsed = useMemo(() => (qr.trim() ? parseInvoiceQR(qr.trim()) : null), [qr]);
  const rows = parsed?.ok ? compareQrToBill(parsed.fields, bill, suppliers[bill?.supplier_id]) : [];

  return (
    <Card className="min-w-0 overflow-hidden" data-testid="purchase-invoice-verification">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><FileSearch size={18} />Purchase Invoice Verification — التحقق من فواتير المشتريات</CardTitle>
        <p className="text-xs text-muted-foreground">View-only check of supplier invoices against your purchase bills.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {LINKS.map(([tid, label, href]) => (
            <Button key={tid} asChild variant="outline" size="sm"><a href={href} target="_blank" rel="noopener noreferrer" data-testid={tid}>{label}<ExternalLink size={13} className="ml-1" /></a></Button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-4 [&>*]:min-w-0">
          <div className="space-y-2">
            <Label className="text-xs">Purchase bill (pick a supplier for its last 12 months; otherwise last 30 days)</Label>
            <select value={supplierId} onChange={(e) => { setSupplierId(e.target.value); setBillId(''); }} className="w-full h-9 rounded-md border border-border bg-background text-sm px-2" data-testid="piv-supplier-select">
              <option value="">All suppliers</option>
              {Object.values(suppliers).sort((a, b) => String(a.name).localeCompare(String(b.name))).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier, invoice no. or bill no." data-testid="piv-bill-search" />
            <select value={billId} onChange={(e) => setBillId(e.target.value)} size={6} className="w-full rounded-md border border-border bg-background text-xs p-1" data-testid="piv-bill-select">
              {shown.map((b) => <option key={b.id} value={b.id}>{(b.issue_date || '').slice(0, 10)} · {b.supplier_name} · #{b.invoice_number || b.bill_number} · {Number(b.total || 0).toFixed(2)}</option>)}
            </select>
          </div>
          <QrInput value={qr} onChange={setQr} />
        </div>
        {parsed && <VerificationResult rows={rows} verdict={overall(bill ? rows : [], parsed)} parsed={parsed} />}
      </CardContent>
    </Card>
  );
};
